// Canonical MSQA form content + its SHA-256 - the PHONE side of
// src/wcc_viewer/msqa.py normalise / canonical_json / content_hash
// (2026-10-13). The monitor's and the contractor's signatures carry this
// hash; the server recomputes it from what it receives and refuses a submit
// whose signatures were made over different content.
//
// Rules (identical on both sides - tests/test_msqa_forms.py and
// frontend/tests/msqaCanonical.test.ts pin the same vector):
//   * strings: Unicode NFC, \r\n and \r -> \n, trimmed; empty -> dropped
//   * numbers -> strings (integers without ".0"); booleans kept
//   * null / "" / [] / {} dropped (recursively); lists keep their order
//   * objects: keys sorted by code point; compact JSON (no spaces)
//   * hashed document: {content, form_id, template, template_version (string)}

export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

function normScalar(v: unknown): Json | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return undefined;
    return String(v); // integers without ".0", like the server
  }
  const s = String(v).normalize("NFC").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  return s ? s : undefined;
}

function isEmpty(v: Json | undefined): boolean {
  if (v === undefined || v === null) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

export function normalise(o: unknown): Json | undefined {
  if (Array.isArray(o)) {
    const out = o.map((x) => normalise(x)).filter((x) => !isEmpty(x)) as Json[];
    return out;
  }
  if (o !== null && typeof o === "object") {
    const out: { [k: string]: Json } = {};
    for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
      const nv = normalise(v);
      if (isEmpty(nv)) continue;
      out[String(k)] = nv as Json;
    }
    return out;
  }
  return normScalar(o);
}

/** Code-point order (what Python's sort_keys does), not UTF-16 unit order. */
function cmpKeys(a: string, b: string): number {
  const A = Array.from(a);
  const B = Array.from(b);
  const n = Math.min(A.length, B.length);
  for (let i = 0; i < n; i++) {
    const d = (A[i].codePointAt(0) ?? 0) - (B[i].codePointAt(0) ?? 0);
    if (d) return d;
  }
  return A.length - B.length;
}

export function canonicalJson(v: Json): string {
  if (v === null) return "null";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(",")}]`;
  const keys = Object.keys(v).sort(cmpKeys);
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(v[k])}`).join(",")}}`;
}

// Hash schemes (row #84, msqa.py HASH_SCHEME): 1 hashes all of the content,
// the signers' typed details included, so a contractor correcting their
// company cleared the monitor's signature too. 2 leaves `signers` out of
// the form hash and binds each signer's own details into that signer's
// signature (signerDigest). A signature with no hash_scheme is scheme 1.
export const HASH_SCHEME = 2;
export const SIGNER_KEYS = ["name", "company", "position"] as const;
export type SignerDetails = { name?: string; company?: string; position?: string };

export function hashDocument(formId: string, templateKey: string, templateVersion: number, content: unknown, scheme = 1): Json {
  const c = (normalise(content) ?? {}) as { [k: string]: Json };
  const base = { form_id: formId, template: templateKey, template_version: String(Math.trunc(templateVersion)) };
  if (scheme === 1) return { ...base, content: c };
  if (scheme !== 2) throw new Error(`unknown hash scheme ${scheme}`);
  const rest = Object.fromEntries(Object.entries(c).filter(([k]) => k !== "signers"));
  return { ...base, hash_scheme: "2", content: rest };
}

/** Scheme 2: the document a signer digest hashes - the form hash, the role and that signer's own details. */
export function signerDocument(formHash: string, role: string, details: SignerDetails | undefined): Json {
  const d: Record<string, unknown> = {};
  for (const k of SIGNER_KEYS) d[k] = details?.[k];
  return { content_hash: formHash, hash_scheme: "2", role, signer: (normalise(d) ?? {}) as Json };
}

function hex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** SubtleCrypto needs a secure context (https or localhost). Without it the
 * phone signs without a hash and the server computes it at submit (recorded
 * as hash_source "server"). */
export function canHash(): boolean {
  return typeof crypto !== "undefined" && !!crypto.subtle && typeof crypto.subtle.digest === "function";
}

export async function sha256Hex(data: string | ArrayBuffer | Uint8Array): Promise<string> {
  const bytes = typeof data === "string" ? new TextEncoder().encode(data) : data instanceof Uint8Array ? data : new Uint8Array(data);
  return hex(await crypto.subtle.digest("SHA-256", bytes as BufferSource));
}

export async function contentHash(formId: string, templateKey: string, templateVersion: number, content: unknown, scheme = 1): Promise<string | null> {
  if (!canHash()) return null;
  return sha256Hex(canonicalJson(hashDocument(formId, templateKey, templateVersion, content, scheme)));
}

export async function signerDigest(formHash: string | null, role: string, details: SignerDetails | undefined): Promise<string | null> {
  if (!canHash() || !formHash) return null;
  return sha256Hex(canonicalJson(signerDocument(formHash, role, details)));
}

export async function blobSha256(b: Blob): Promise<string | null> {
  if (!canHash()) return null;
  return sha256Hex(await b.arrayBuffer());
}
